import React from "react";
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getAllCalendarData } from "../../models/calendar";
import i18n from "../../i18n";
import { getLoggedUser } from "../../state/user";
import { SaudiRiyalLogo } from "../logo/saudiLogo";
import { formatCurrency } from "../../state/utils";
import '../../styles/components/auction-calendar.scss';

const user = getLoggedUser();

const HomeBanner = ({ callbackForSupport }) => (
    <div className="hero-banner">
        <div className="hero-content">
            <h1 className="hero-title">{i18n.t('auction_calendar')}</h1>
        </div>
    </div>
);

export class AuctionCalendar extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            eventsList: [],
            currentMonth: new Date(),
            selectedDate: new Date(),
            searchTerm: '',
            selectedSeller: 'Seller',
            selectedLocation: 'Location',
            loading: true
        };

        this.getAllData = this.getAllData.bind(this);
        this.navigateMonth = this.navigateMonth.bind(this);
        this.goToToday = this.goToToday.bind(this);
        this.selectDate = this.selectDate.bind(this);
        this.handleSelectEvent = this.handleSelectEvent.bind(this);
    }

    componentDidMount() {
        window.scrollTo(0, 0);
        this.getAllData();
    }

    createDate(dt) {
        if (!dt) return null;
        if (dt instanceof Date) return dt;
        return new Date(dt.replace ? dt.replace(/-/g, "/") : dt);
    }

    async getAllData() {
        this.setState({ loading: true });
        try {
            const response = await getAllCalendarData();
            if (response.success) {
                this.setState({
                    eventsList: response.data.map(event => ({
                        ...event,
                        start: this.createDate(event.start),
                        end: this.createDate(event.end)
                    })),
                    loading: false
                });
            }
        } catch (error) {
            console.error('Failed to load calendar data:', error);
            this.setState({ loading: false });
        }
    }

    handleSelectEvent(event) {
        window.location.href = "/auction-details/" + event.data.id;
    }

    getMonthDays = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();

        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);

        const startDay = new Date(firstDay);
        const dayOfWeek = firstDay.getDay();
        const diff = firstDay.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
        startDay.setDate(diff);

        const endDay = new Date(lastDay);
        const lastDayOfWeek = lastDay.getDay();
        const diffEnd = lastDay.getDate() + (7 - lastDayOfWeek) - (lastDayOfWeek === 0 ? 7 : 0);
        endDay.setDate(diffEnd);

        const days = [];
        const current = new Date(startDay);

        while (current <= endDay) {
            days.push(new Date(current));
            current.setDate(current.getDate() + 1);
        }

        return days;
    };

    formatMonthYear = (date) => {
        return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    };

    getAuctionsForDate = (date) => {
        return this.state.eventsList.filter(event => {
            const eventDate = new Date(event.start);
            return eventDate.toDateString() === date.toDateString();
        });
    };

    navigateMonth = (direction) => {
        const newMonth = new Date(this.state.currentMonth);
        newMonth.setMonth(newMonth.getMonth() + (direction === 'next' ? 1 : -1));
        this.setState({ currentMonth: newMonth });
    };

    goToToday = () => {
        const today = new Date();
        this.setState({
            currentMonth: today,
            selectedDate: today
        });
    };

    selectDate = (date) => {
        this.setState({ selectedDate: date });
    };

    isCurrentMonth = (date) => {
        return date.getMonth() === this.state.currentMonth.getMonth() &&
            date.getFullYear() === this.state.currentMonth.getFullYear();
    };

    isToday = (date) => {
        const today = new Date();
        return date.toDateString() === today.toDateString();
    };

    getSidebarAuctions = () => {
        const selectedDateAuctions = this.getAuctionsForDate(this.state.selectedDate);
        return selectedDateAuctions.slice(0, 6).map(auction => ({
            id: auction.data?.id || auction.id || 'N/A',
            title: auction.title,
            reg_start_date: auction.data.reg_start_date,
            reg_end_date: auction.data.reg_end_date,
            start_date: auction.data.start_date,
            end_date: auction.data.end_date,
            guarantee_amount: auction.data.guarantee_amount,
            start_amount: auction.data.start_amount,
            latitude: auction.data?.latitude,
            longitude: auction.data?.longitude
        }));
    };

    getMonthStats = () => {
        const monthEvents = this.state.eventsList.filter(event => {
            const eventDate = new Date(event.start);
            return eventDate.getMonth() === this.state.currentMonth.getMonth() &&
                eventDate.getFullYear() === this.state.currentMonth.getFullYear();
        });

        return {
            totalAuctions: monthEvents.length,
            totalDays: new Set(monthEvents.map(event => new Date(event.start).toDateString())).size
        };
    };

    render() {
        const monthDays = this.getMonthDays(this.state.currentMonth);
        const weeks = [];

        for (let i = 0; i < monthDays.length; i += 7) {
            weeks.push(monthDays.slice(i, i + 7));
        }

        const selectedDateAuctions = this.getAuctionsForDate(this.state.selectedDate);
        const sidebarAuctions = this.getSidebarAuctions();
        const monthStats = this.getMonthStats();
        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

        return (
            <React.Fragment>
                <HomeBanner callbackForSupport={this.props.callbackForSupport} />
                <div className="main-content">
                    <div className="auction-calendar">
                        <div className="auction-calendar__container">
                            {/* Calendar Section */}
                            <div className="auction-calendar-row">
                                {/* Calendar */}
                                <div className="auction-calendar__calendar-section">
                                    <div className="auction-calendar__calendar-header">
                                        <div className="auction-calendar__nav-group">
                                            <button
                                                className="auction-calendar__nav-button"
                                                onClick={() => this.navigateMonth('prev')}
                                            >
                                                <ChevronLeft size={24} />
                                            </button>
                                            <div className="auction-calendar__month-info">
                                                <div className="auction-calendar__month-title">
                                                    {this.formatMonthYear(this.state.currentMonth)}
                                                </div>
                                                <div className="auction-calendar__month-stats">
                                                    <div className="auction-calendar__stat-item">
                                                        {monthStats.totalAuctions} auctions
                                                    </div>
                                                    <div className="auction-calendar__stat-item">
                                                        {monthStats.totalDays} days with auctions
                                                    </div>
                                                </div>
                                            </div>
                                            <button
                                                className="auction-calendar__nav-button"
                                                onClick={() => this.navigateMonth('next')}
                                            >
                                                <ChevronRight size={24} />
                                            </button>
                                        </div>

                                        <button
                                            className="auction-calendar__today-button"
                                            onClick={this.goToToday}
                                        >
                                            Today
                                        </button>
                                    </div>

                                    {/* Day Headers */}
                                    <div className="auction-calendar__calendar-grid">
                                        {dayNames.map((dayName, index) => (
                                            <div key={index} className="auction-calendar__day-header">
                                                {dayName}
                                            </div>
                                        ))}
                                    </div>

                                    {/* Calendar Grid */}
                                    {this.state.loading ? (
                                        <div className="auction-calendar__loading-container">
                                            {i18n.t('loading_auction')}
                                        </div>
                                    ) : (
                                        <div className="auction-calendar__calendar-grid">
                                            {monthDays.map((day, index) => {
                                                const isCurrentMonth = this.isCurrentMonth(day);
                                                const isToday = this.isToday(day);
                                                const isSelected = day.toDateString() === this.state.selectedDate.toDateString();
                                                const dayAuctions = this.getAuctionsForDate(day);

                                                const cellClasses = [
                                                    'auction-calendar__day-cell',
                                                    !isCurrentMonth && 'auction-calendar__day-cell--other-month',
                                                    isToday && 'auction-calendar__day-cell--today',
                                                    isSelected && 'auction-calendar__day-cell--selected'
                                                ].filter(Boolean).join(' ');

                                                return (
                                                    <div
                                                        key={index}
                                                        className={cellClasses}
                                                        onClick={() => this.selectDate(day)}
                                                        dir="ltr"
                                                    >
                                                        <div className="auction-calendar__day-number">
                                                            {day.getDate()}
                                                        </div>
                                                        {dayAuctions.length > 0 && (
                                                            <div className="auction-calendar__auction-count">
                                                                {dayAuctions.length}
                                                            </div>
                                                        )}
                                                        <div style={{ maxHeight: '70px', overflowY: 'auto' }}>
                                                            {dayAuctions.length > 3 && (
                                                                <div className="auction-calendar__more-auctions">
                                                                    +{dayAuctions.length - 3} more
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                {/* Sidebar */}
                                <div className="auction-calendar__sidebar">
                                    <div className="auction-calendar__sidebar-header">
                                        {i18n.t('selected_date')}
                                    </div>
                                    <div className="auction-calendar__sidebar-date">
                                        {this.state.selectedDate.toLocaleDateString('en-US', {
                                            weekday: 'long',
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric'
                                        })}
                                    </div>

                                    {selectedDateAuctions.length > 0 ? (
                                        sidebarAuctions.map((auction, index) => (
                                            <div
                                                key={index}
                                                className="auction-calendar__auction-item"
                                                onClick={() => {
                                                    const fullAuction = this.state.eventsList.find(a =>
                                                        (a.data?.id || a.id) === auction.id
                                                    );
                                                    if (fullAuction) this.handleSelectEvent(fullAuction);
                                                }}
                                            >
                                                <div className="auction-calendar__auction-id">
                                                    {auction.latitude && auction.longitude && (
                                                        <a
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            href={`https://www.google.com/maps?q=${auction.latitude},${auction.longitude}`}
                                                            className="location-link"
                                                            style={{ fontSize: "14px", marginRight: "6px" }}
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <i className="fa fa-location-arrow"></i>
                                                        </a>
                                                    )}
                                                    MZADSA{auction.id}
                                                </div>
                                                <div className="auction-calendar__auction-title">
                                                    {auction.title}
                                                </div>

                                                <div className="auction-calendar__auction-dates">
                                                    <div className="auction-calendar__date-group">
                                                        <h6 className="auction-calendar__auction-title auction-calendar__auction-title--bold">
                                                            {i18n.t('registration_start')}
                                                        </h6>
                                                        <div className="auction-calendar__auction-title">
                                                            {new Date(this.createDate(auction.reg_start_date)).toLocaleDateString('en-GB', {
                                                                day: '2-digit',
                                                                month: 'short',
                                                                year: 'numeric',
                                                                hour: '2-digit',
                                                                minute: '2-digit'
                                                            })}
                                                        </div>
                                                    </div>

                                                    <div className="auction-calendar__date-group">
                                                        <h6 className="auction-calendar__auction-title auction-calendar__auction-title--bold">
                                                            {i18n.t('registration_end')}
                                                        </h6>
                                                        <div className="auction-calendar__auction-title">
                                                            {new Date(this.createDate(auction.reg_end_date)).toLocaleDateString('en-GB', {
                                                                day: '2-digit',
                                                                month: 'short',
                                                                year: 'numeric',
                                                                hour: '2-digit',
                                                                minute: '2-digit'
                                                            })}
                                                        </div>
                                                    </div>

                                                    <div className="auction-calendar__date-group">
                                                        <h6 className="auction-calendar__auction-title auction-calendar__auction-title--bold">
                                                            {i18n.t('start_date')}
                                                        </h6>
                                                        <div className="auction-calendar__auction-title">
                                                            {new Date(this.createDate(auction.start_date)).toLocaleDateString('en-GB', {
                                                                day: '2-digit',
                                                                month: 'short',
                                                                year: 'numeric',
                                                                hour: '2-digit',
                                                                minute: '2-digit'
                                                            })}
                                                        </div>
                                                    </div>

                                                    <div className="auction-calendar__date-group">
                                                        <h6 className="auction-calendar__auction-title auction-calendar__auction-title--bold">
                                                            {i18n.t('end_date')}
                                                        </h6>
                                                        <div className="auction-calendar__auction-title">
                                                            {new Date(this.createDate(auction.end_date)).toLocaleDateString('en-GB', {
                                                                day: '2-digit',
                                                                month: 'short',
                                                                year: 'numeric',
                                                                hour: '2-digit',
                                                                minute: '2-digit'
                                                            })}
                                                        </div>
                                                    </div>

                                                    <div className="auction-calendar__date-group">
                                                        <h6 className="auction-calendar__auction-title auction-calendar__auction-title--bold">
                                                            {i18n.t('gurantee_amount')}
                                                        </h6>
                                                        <div className="auction-calendar__auction-title">
                                                            {formatCurrency(auction.guarantee_amount)} <SaudiRiyalLogo />
                                                        </div>
                                                    </div>

                                                    <div className="auction-calendar__date-group">
                                                        <h6 className="auction-calendar__auction-title auction-calendar__auction-title--bold">
                                                            {i18n.t('start_bid_amount')}
                                                        </h6>
                                                        <div className="auction-calendar__auction-title">
                                                            {formatCurrency(auction.start_amount)} <SaudiRiyalLogo />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="auction-calendar__no-events">
                                            No auctions scheduled for this date
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </React.Fragment>
        );
    }
}